import React from 'react';
import { CalendarIcon } from 'components/common/icons';
import { AdminSelect, BrandSelect, FilterDrawer } from 'components/common';
import { Col, DatePicker, Form, FormInstance, Input, Row, Select, SupplierSelect } from 'components';
import { useFilters } from './useFilters';
import { InputNumber, Tag } from 'antd';
import { preventNonNumericValue } from 'utils/helpers';
import styles from 'feature/purchaseRequestTabs/supplierOffers/styles.module.scss';
import { SupplierOfferType } from 'models/SupplierOffer';

const { RangePicker } = DatePicker;

interface IFilters {
  form: FormInstance;
  drawerOpen: boolean;
  closeDrawer: () => void;
  applyFilters: (value: Record<string, any>) => void;
  resetFilters: () => void;
}

type LinkedEntityOptionType = {
  label: string;
  value: SupplierOfferType;
};
type StatusOptionType = {
  label: string;
  value: boolean;
};

const LINKED_ENTITY_OPTIONS: Array<LinkedEntityOptionType> = [
  { value: 'PURCHASE_ORDER', label: 'Purchase Order' },
  { value: 'PURCHASE_REQUEST', label: 'Purchase Request' },
  { value: 'NONE', label: 'None' },
];
const STATUS_OPTIONS: Array<StatusOptionType> = [
  { value: false, label: 'Draft' },
  { value: true, label: 'Publish' },
];

export const Filters = React.memo((props: IFilters) => {
  const { form, drawerOpen, closeDrawer, applyFilters, resetFilters } = props;
  const { clearFilters } = useFilters({ form, resetFilters });

  return (
    <FilterDrawer
      title="Filter"
      placement="left"
      width={471}
      styles={{
        body: {
          marginBottom: 80,
        },
      }} // Add space for drawer actions
      onClose={closeDrawer}
      open={drawerOpen}
      onApply={() => form.submit()}
      onClear={clearFilters}
      onExpand={closeDrawer}
    >
      <Form layout="vertical" form={form} onFinish={applyFilters} initialValues={{ leadTime: 'WEEK' }}>
        <Row gutter={[0, 16]}>
          <Col span={24}>
            <Form.Item name="supplierId" label={'Supplier'}>
              <SupplierSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="brandId" label={'Brand'}>
              <BrandSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Row gutter={16} wrap={false} align={'bottom'}>
              <Col span={12}>
                <Form.Item name="totalAmountMin" label={'Total Amount'}>
                  <Input placeholder={'Min'} type={'number'} />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name="totalAmountMax">
                  <Input placeholder={'Max'} type={'number'} />
                </Form.Item>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <Form.Item name="type" label={'Linked Entity'}>
              <Select options={LINKED_ENTITY_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="createDate" label={'Created Date'}>
              <RangePicker suffixIcon={<CalendarIcon />} className={'w-100'} popupClassName={'fmg-range-picker'} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="createdBy" label={'Created By'}>
              <AdminSelect />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="published" label={'Status'}>
              <Select options={STATUS_OPTIONS} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="supplierOfferValidity" label={'Valid Until'}>
              <RangePicker suffixIcon={<CalendarIcon />} className={'w-100'} popupClassName={'fmg-range-picker'} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Row gutter={8}>
              <Col span={12}>
                <Form.Item name={'leadTimeMin'} label={'Lead Time'}>
                  <InputNumber<number>
                    controls={false}
                    size={'middle'}
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                    placeholder={'Min'}
                  />
                </Form.Item>
              </Col>
              <Col span={12}>
                <Form.Item name={'leadTimeMax'} label={<span style={{ display: 'none' }}></span>}>
                  <InputNumber<number>
                    controls={false}
                    size={'middle'}
                    onBeforeInput={(event: any) => preventNonNumericValue(event)}
                    style={{ width: '100%' }}
                    suffix={
                      <Form.Item shouldUpdate noStyle>
                        {({ getFieldValue }) => {
                          const type = getFieldValue('leadTime');
                          return (
                            <Form.Item name={'leadTime'}>
                              <Row className="gap-4">
                                <Tag
                                  className={`${styles.tag} ${type === 'DAY' && styles.selected_tag}`}
                                  onClick={(event) => {
                                    form.setFieldValue('leadTime', 'DAY');
                                    event.stopPropagation();
                                  }}
                                >
                                  Day
                                </Tag>
                                <Tag
                                  className={`${styles.tag} ${type === 'WEEK' && styles.selected_tag}`}
                                  onClick={(event) => {
                                    form.setFieldValue('leadTime', 'WEEK');
                                    event.stopPropagation();
                                  }}
                                >
                                  Week
                                </Tag>
                              </Row>
                            </Form.Item>
                          );
                        }}
                      </Form.Item>
                    }
                    placeholder={'Max'}
                  />
                </Form.Item>
              </Col>
            </Row>
          </Col>
        </Row>
      </Form>
    </FilterDrawer>
  );
});
